import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import React, { useState, useRef, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { CONSUME_EMAIL_OTP, REQUEST_MAGIC_LINK } from "./magiclink.gql";
import BrowserPersistence from "@overrides_venia/peregrine/util/simplePersistence";
import { parseJwt } from "@Src_main/utils/generalUtils";
import { useUserContext } from "@overrides_venia/peregrine/context/user";
import { useIntl } from "react-intl";
import Button from "@overrides_venia/venia-ui/components/Button";
import { mergeClasses } from "@magento/venia-ui/lib/classify";
import defaultClasses from "./otpForm.module.scss";
import { useAwaitQuery } from "@magento/peregrine/lib/hooks/useAwaitQuery";
import { GET_CUSTOMER } from "@overrides_venia/peregrine/talons/SignIn/signIn.gql";
var OtpForm = _ref => {
  var {
    email,
    onSuccess,
    onError,
    classes: propsClasses,
    type = "LOGIN"
  } = _ref;
  var classes = mergeClasses(defaultClasses, propsClasses);
  var {
    formatMessage
  } = useIntl();
  var [otp, setOtp] = useState(["", "", "", "", "", ""]);
  var [error, setError] = useState("");
  var [successMessage, setSuccessMessage] = useState("");
  var [isSubmitting, setIsSubmitting] = useState(false);
  var [isResending, setIsResending] = useState(false);
  var [resendCooldown, setResendCooldown] = useState(0);
  var inputRefs = useRef([]);
  var storage = new BrowserPersistence();
  var [, {
    setToken,
    getUserDetails
  }] = useUserContext();
  var fetchUserDetails = useAwaitQuery(GET_CUSTOMER);
  var [verifyOtp] = useMutation(CONSUME_EMAIL_OTP, {
    fetchPolicy: "no-cache"
  });
  var [requestMagicLink] = useMutation(REQUEST_MAGIC_LINK, {
    fetchPolicy: "no-cache"
  });
  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      var timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);
  var handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }
    var newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    // Auto-focus next input
    if (value && index < 5) {
      var _inputRefs$current;
      (_inputRefs$current = inputRefs.current[index + 1]) === null || _inputRefs$current === void 0 ? void 0 : _inputRefs$current.focus();
    }

    // Auto-submit if all 6 digits are filled
    if (newOtp.every(digit => digit !== "")) {
      setTimeout(() => {
        handleSubmit(new Event("submit"), newOtp.join(""));
      }, 100);
    }
  };
  var handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      var _inputRefs$current2;
      // Move to previous input on backspace if current is empty
      (_inputRefs$current2 = inputRefs.current[index - 1]) === null || _inputRefs$current2 === void 0 ? void 0 : _inputRefs$current2.focus();
    } else if (e.key === "Enter") {
      handleSubmit(e);
    }
  };
  var handlePaste = e => {
    var _inputRefs$current$la;
    e.preventDefault();
    var pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) {
      return;
    }
    var newOtp = [...otp];
    pastedData.split("").forEach((char, index) => {
      if (index < 6) {
        newOtp[index] = char;
      }
    });
    setOtp(newOtp);

    // Focus last filled input or submit if complete
    var lastIndex = Math.min(pastedData.length - 1, 5);
    (_inputRefs$current$la = inputRefs.current[lastIndex]) === null || _inputRefs$current$la === void 0 ? void 0 : _inputRefs$current$la.focus();

    // Auto-submit if complete
    if (pastedData.length === 6) {
      setTimeout(() => {
        handleSubmit(e, pastedData);
      }, 100);
    }
  };
  var handleSubmit = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(function* (e) {
      var otpCode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
      e.preventDefault();
      setIsSubmitting(true);
      setError("");
      var code = otpCode || otp.join("");
      if (code.length !== 6) {
        setError(formatMessage({
          id: "otp.error.incomplete"
        }));
        setIsSubmitting(false);
        return;
      }
      try {
        var _response$data, _response$data$verify;
        var response = yield verifyOtp({
          variables: {
            email,
            code
          }
        });
        var token = (_response$data = response.data) === null || _response$data === void 0 ? void 0 : (_response$data$verify = _response$data.verifyEmailOtp) === null || _response$data$verify === void 0 ? void 0 : _response$data$verify.token;
        if (token) {
          // Store token in local storage with expiration
          var jwtDecoded = parseJwt(token);
          var ttlToken = Math.floor((jwtDecoded.exp * 1000 - Date.now()) / 1000);

          // Check if TTL is valid
          if (ttlToken <= 0) {
            throw new Error("Token has already expired");
          }
          storage.setItem("signin_token", token, ttlToken);

          // Update user context
          yield setToken(token);

          // Fetch user details with the query function
          yield getUserDetails({
            fetchUserDetails
          });
          if (onSuccess) {
            onSuccess(token);
          }
        } else {
          throw new Error("No token received");
        }
      } catch (err) {
        var _err$message, _err$message2, _err$message3, _inputRefs$current$;
        console.error("OTP verification error:", err);
        var errorMessage;
        if ((_err$message = err.message) !== null && _err$message !== void 0 && _err$message.includes("Invalid") || (_err$message2 = err.message) !== null && _err$message2 !== void 0 && _err$message2.includes("invalid") || (_err$message3 = err.message) !== null && _err$message3 !== void 0 && _err$message3.includes("incorrect")) {
          errorMessage = formatMessage({
            id: "otp.error.invalid"
          });
        } else if (err.message === "Token has already expired") {
          errorMessage = formatMessage({
            id: "otp.error.token.expired"
          });
        } else if (err.graphQLErrors && err.graphQLErrors.length > 0) {
          // Handle GraphQL errors
          var gqlError = err.graphQLErrors[0];
          errorMessage = formatMessage({
            id: "otp.error.invalid"
          });
        } else {
          errorMessage = formatMessage({
            id: "otp.error.generic"
          });
        }
        setError(errorMessage);
        setOtp(["", "", "", "", "", ""]);
        (_inputRefs$current$ = inputRefs.current[0]) === null || _inputRefs$current$ === void 0 ? void 0 : _inputRefs$current$.focus();
        if (onError) {
          onError(err);
        }
      } finally {
        setIsSubmitting(false);
      }
    });
    return function handleSubmit(_x) {
      return _ref2.apply(this, arguments);
    };
  }();
  var handleResendCode = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(function* () {
      setIsResending(true);
      setError("");
      try {
        var _response$data2, _response$data2$reque;
        var response = yield requestMagicLink({
          variables: {
            email,
            type
          }
        });
        if ((_response$data2 = response.data) !== null && _response$data2 !== void 0 && (_response$data2$reque = _response$data2.requestMagicLink) !== null && _response$data2$reque !== void 0 && _response$data2$reque.success) {
          setResendCooldown(60); // 60 second cooldown
          setError("");
          setSuccessMessage(formatMessage({
            id: "otp.resent.success"
          }));
          // Auto-dismiss success message after 3 seconds
          setTimeout(() => {
            setSuccessMessage("");
          }, 3000);
        } else {
          var _response$data3, _response$data3$reque;
          var message = (_response$data3 = response.data) === null || _response$data3 === void 0 ? void 0 : (_response$data3$reque = _response$data3.requestMagicLink) === null || _response$data3$reque === void 0 ? void 0 : _response$data3$reque.message;
          throw new Error(message || "Failed to resend code");
        }
      } catch (err) {
        console.error("Resend code error:", err);
        setError(err.message || formatMessage({
          id: "otp.resend.error"
        }));
      } finally {
        setIsResending(false);
      }
    });
    return function handleResendCode() {
      return _ref3.apply(this, arguments);
    };
  }();
  return /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit,
    className: classes.otpForm
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.otpContainer
  }, /*#__PURE__*/React.createElement("h3", {
    className: classes.title
  }, formatMessage({
    id: "otp.title"
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.inputGroup
  }, otp.map((digit, index) => /*#__PURE__*/React.createElement("input", {
    key: index,
    ref: el => inputRefs.current[index] = el,
    type: "text",
    inputMode: "numeric",
    maxLength: 1,
    value: digit,
    placeholder: "\u2022",
    onChange: e => handleChange(index, e.target.value),
    onKeyDown: e => handleKeyDown(index, e),
    onPaste: handlePaste,
    className: "".concat(classes.otpInput, " ").concat(error ? classes.error : ""),
    disabled: isSubmitting,
    autoComplete: "off"
  }))), error && /*#__PURE__*/React.createElement("div", {
    className: classes.errorMessage
  }, error), /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    disabled: isSubmitting || otp.some(d => !d),
    fullScreen: true,
    color: "primary"
  }, isSubmitting ? formatMessage({
    id: "otp.verifying"
  }) : formatMessage({
    id: "otp.verify"
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: "1rem"
    }
  }), /*#__PURE__*/React.createElement(Button, {
    type: "button",
    onClick: handleResendCode,
    disabled: isResending || resendCooldown > 0,
    fullScreen: true,
    color: "outline_primary"
  }, isResending ? formatMessage({
    id: "otp.resending"
  }) : resendCooldown > 0 ? "".concat(formatMessage({
    id: "otp.resend.wait"
  }), " ").concat(resendCooldown, " ").concat(formatMessage({
    id: "seconds"
  })) : formatMessage({
    id: "otp.resend"
  }))));
};
export default OtpForm;